import type { CompiledOp } from "../effects/face-chain.js";
import type { GustDraw, GustPoint } from "./gust.js";

/** What started a wave. Every source shares the same limit on live waves (`RippleSystem`). */
export type RippleSource = "ambient" | "click" | "theme";

export interface Ripple {
  readonly source: RippleSource;
  readonly t0: number;
  readonly amp: number;
  readonly strength: number; // flap strength from 0 to 1; sets the wave's speed
  readonly level: number; // effect level 1 to 5 (from clicks or flap strength); picks effects
  readonly faceOps: readonly CompiledOp[]; // the face's effects (face-chain.ts); level 3 and up
  readonly gust: GustDraw; // the front's random shape (gust.ts)
  readonly full: number; // the mean radius where the front ends (`gustFullRadius`)
}

export interface RippleState {
  items: Ripple[]; // owned by `RippleSystem`
  /** The points a wave's band has to pass before the wave ends: the viewport's corners and the
      portrait's, in pixels from the wave's origin. The page's layout code sets them. */
  exitPoints: readonly GustPoint[];
  /** While this returns true, no new wave starts from any source. The host holds them (the
      site's dust sweep runs one at a time). A click still flaps, just without a wave. */
  held?: () => boolean;
}

export interface World {
  time: number; // seconds
  reducedMotion: boolean;
  ripples: RippleState;
}

export interface System {
  update(world: World, dt: number): void;
}

export interface WorldOverrides {
  time?: number;
  reducedMotion?: boolean;
  ripples?: Partial<RippleState>;
}

// A 1500px square around the origin, until the page's layout code measures the real thing.
const DEFAULT_EXIT_POINTS: readonly GustPoint[] = [
  { x: -750, y: -750 },
  { x: 750, y: -750 },
  { x: -750, y: 750 },
  { x: 750, y: 750 },
];

export function createWorld(o: WorldOverrides = {}): World {
  return {
    time: o.time ?? 0,
    reducedMotion: o.reducedMotion ?? false,
    ripples: { items: [], exitPoints: DEFAULT_EXIT_POINTS, ...o.ripples },
  };
}
